import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { pdfjsLib } from '../../services/pdfWorker';
import { PdfControls } from './PdfControls';
import { DrawingCanvas } from './DrawingCanvas';
import { HighlightOverlay } from './HighlightOverlay';

export const PdfViewer: React.FC = () => {
  const {
    currentFileBlob,
    pdfScale,
    currentPageIndex,
    setNumPages,
    appMode,
    activeBbox,
  } = useApp();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const pdfCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pdfDocRef = useRef<any>(null);
  const loadedBlobRef = useRef<Blob | null>(null);
  const renderTaskRef = useRef<any>(null);

  const [viewportWidth, setViewportWidth] = useState<number>(0);
  const [viewportHeight, setViewportHeight] = useState<number>(0);
  const [selectedStringOption, setSelectedStringOption] = useState<string>('');
  const [isDrawingActive, setIsDrawingActive] = useState<boolean>(false);
  const finishStringRef = useRef<(() => void) | null>(null);

  // Render the current PDF page onto the PDF canvas
  const renderPage = useCallback(
    async (pageIdx: number) => {
      if (!pdfDocRef.current || !pdfCanvasRef.current) return;

      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // Ignore cancellation
        }
      }

      try {
        const page = await pdfDocRef.current.getPage(pageIdx + 1);
        const viewport = page.getViewport({ scale: pdfScale });

        const canvas = pdfCanvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        setViewportWidth(viewport.width);
        setViewportHeight(viewport.height);

        const renderContext = {
          canvasContext: ctx,
          viewport,
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;
        await renderTask.promise;
      } catch (err: any) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Error rendering PDF page:', err);
        }
      }
    },
    [pdfScale]
  );

  // Load PDF when currentFileBlob changes
  useEffect(() => {
    if (!currentFileBlob) {
      pdfDocRef.current = null;
      loadedBlobRef.current = null;
      setViewportWidth(0);
      setViewportHeight(0);
      return;
    }

    let isCancelled = false;

    const loadDoc = async () => {
      try {
        const arrayBuffer = await currentFileBlob.arrayBuffer();
        if (isCancelled) return;

        const loadingTask = pdfjsLib.getDocument(arrayBuffer);
        const doc = await loadingTask.promise;
        if (isCancelled) return;

        pdfDocRef.current = doc;
        loadedBlobRef.current = currentFileBlob;
        setNumPages(doc.numPages);
        renderPage(currentPageIndex);
      } catch (err) {
        console.error('Error loading PDF document:', err);
      }
    };

    loadDoc();

    return () => {
      isCancelled = true;
    };
  }, [currentFileBlob, setNumPages, currentPageIndex, renderPage]);

  // Re-render when page index or zoom changes
  useEffect(() => {
    if (pdfDocRef.current) {
      renderPage(currentPageIndex);
    }
  }, [currentPageIndex, pdfScale, renderPage]);

  // Pan interaction handling
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (appMode !== 'SELECT' || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const target = containerRef.current;
    if (!target) return;

    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: target.scrollLeft,
      scrollTop: target.scrollTop,
    };
    target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanning) return;
    const target = containerRef.current;
    if (!target) return;

    target.scrollLeft = panStartRef.current.scrollLeft - (e.clientX - panStartRef.current.x);
    target.scrollTop = panStartRef.current.scrollTop - (e.clientY - panStartRef.current.y);
  };

  const handlePointerUp = () => {
    setIsPanning(false);
  };

  // Scroll active bounding box into view if highlighted
  useEffect(() => {
    if (activeBbox && containerRef.current && viewportHeight > 0) {
      const ys = activeBbox.map((p) => p.y);
      const minY = Math.min(...ys) * viewportHeight;
      const maxY = Math.max(...ys) * viewportHeight;
      const boxHeight = maxY - minY;
      const panelHeight = containerRef.current.clientHeight;
      const targetScroll = minY - panelHeight / 2 + boxHeight / 2;

      containerRef.current.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth',
      });
    }
  }, [activeBbox, viewportHeight]);

  return (
    <section className="left-panel">
      {/* Controls Bar */}
      <PdfControls
        onFinishString={() => finishStringRef.current?.()}
        isDrawingActive={isDrawingActive}
        selectedStringOption={selectedStringOption}
        setSelectedStringOption={setSelectedStringOption}
      />

      {/* Viewport Area */}
      <div
        ref={containerRef}
        className="pdf-viewer-area"
        style={{
          cursor: appMode === 'SELECT' ? (isPanning ? 'grabbing' : 'grab') : 'default',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div
          id="pdfContainer"
          style={{
            position: 'relative',
            display: viewportWidth > 0 ? 'block' : 'none',
          }}
        >
          {/* Base PDF Canvas */}
          <canvas ref={pdfCanvasRef} id="the-canvas" style={{ display: 'block' }} />

          {/* Interactive Measurement & Overlay Canvas */}
          <DrawingCanvas
            viewportWidth={viewportWidth}
            viewportHeight={viewportHeight}
            selectedStringOption={selectedStringOption}
            setSelectedStringOption={setSelectedStringOption}
            isDrawingActive={isDrawingActive}
            setIsDrawingActive={setIsDrawingActive}
            finishStringRef={finishStringRef}
          />

          {/* OCR Document AI Highlight Box */}
          <HighlightOverlay
            bbox={activeBbox}
            canvasWidth={viewportWidth}
            canvasHeight={viewportHeight}
          />
        </div>
      </div>
    </section>
  );
};
